import { LoginFlow } from "@ory/client";
import { useRouter } from "next/router";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { isUiNodeInputAttributes } from "@ory/integrations/ui";

import { useApi, useOry, useToasts } from "~/hooks";
import {
  analytics,
  ButtonClickEvent,
  ScreenShownEvent,
} from "~/libs/analytics";
import { ApiError } from "~/libs/api";
import { urlForPath } from "~/libs/http";
import { ErrorBody } from "~/libs/types";
import { isValidEmail } from "~/libs/utils";
import {
  Box,
  Button,
  Error,
  FormField,
  Icon,
  InputField,
  Separator,
} from "~/ui/components";

import { Step, StepTransition } from "./form";
import { FlowMessage } from "../components/flow-message";
import { FormIntro } from "~/ui/components/form-intro";
import { handleFlowError } from "~/libs/ory";

interface Props {
  flow: LoginFlow;
  nextStep(step: StepTransition): void;
}

const SOCIAL_LOGIN_PROVIDERS = ["apple", "google", "facebook"];

const InputEmail = ({ flow, nextStep }: Props) => {
  const ory = useOry();
  const form = useFormContext();
  const { t } = useTranslation();
  const router = useRouter();
  const { addToast } = useToasts();
  const { inProgress, post } = useApi<{
    data: { providers: string[] };
  }>();
  const [selectedSocialProvider, setSelectedSocialProvider] = useState<string|undefined>(undefined)

  const {
    formState: { errors, touchedFields },
  } = form;

  const onSocialLogin = async (provider: "facebook" | "google" | "apple") => {
    // clear onBlur validation error esp: for email as we don't want it when user clicks social login
    form.clearErrors();
  
    const csrfToken = flow.ui.nodes.reduce((token, node) => {
      const { attributes } = node;
    
      if (isUiNodeInputAttributes(attributes) && attributes.name === 'csrf_token') {
        return attributes.value;
      }

      return token;
    }, undefined);

    if (!flow || !csrfToken) return;

    setSelectedSocialProvider(provider)

    analytics.track(
      new ButtonClickEvent({
        button: `continue with ${provider}`,
        screen: "home",
      }),
    );

    try {
      await ory.updateLoginFlow({
        flow: flow.id,
        updateLoginFlowBody: {
          csrf_token: csrfToken,
          method: "oidc",
          provider,
          upstream_parameters: {
            ...(provider === "apple" && { prompt: "login" }),
            ...(provider === "google" && { prompt: "login" }),
            ...(provider === "facebook" && { auth_type: "reauthenticate" }),
          },
        },
      });

    } catch (error) {
      if (!axios.isAxiosError(error)) throw error;
      await handleFlowError(router, "login")(error);
    }
  };

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      analytics.track(
        new ButtonClickEvent({
          button: "continue",
          screen: "login: email input",
        }),
      );

      const shouldPost = await form.trigger(["identifier"]);

      if (!shouldPost) return;

      try {
        const identifier = form.getValues("identifier");

        const response = await post("/api/login-options", {
          identifier,
        });

        const providers = response?.data?.data.providers || [];

        if (!providers.length) {
          const returnTo = flow.return_to || urlForPath("/api/redirect");
          const { data: registrationFlow } =
            await ory.createBrowserRegistrationFlow({
              loginChallenge: flow.oauth2_login_challenge,
              returnTo,
              afterVerificationReturnTo: returnTo,
            });

          await router.push({
            pathname: "/registration",
            query: {
              flow: registrationFlow.id,
              identifier,
            },
          });
          return;
        }

        if (providers.includes("password")) {
          nextStep({
            from: Step.InputEmail,
            to: Step.InputPassword,
            payload: { email: identifier },
          });
          return;
        }

        if (providers.some((item) => SOCIAL_LOGIN_PROVIDERS.includes(item))) {
          nextStep({
            from: Step.InputEmail,
            to: Step.SelectProvider,
            payload: {
              email: identifier,
              loginProviders: providers,
            },
          });
        }
      } catch (err) {
        if (!(err instanceof ApiError)) throw err;
        const error: ErrorBody = err.response.error;

        addToast({
          title: error.title,
          description: error.detail,
        });
      }
    },
    [addToast, flow, form, nextStep, ory, post, router, t],
  );

  const screenShown = useCallback(() => {
    analytics.track(
      new ScreenShownEvent({
        screen: "login: email input",
      }),
    );
  }, []);

  useEffect(() => screenShown(), [screenShown]);

  return (
    <Box space="large">
      <form onSubmit={onSubmit} noValidate>
        <Box space="large" textCenter>
          <FormIntro
            title={t("login.default.heading")}
            intro={t("login.default.introductory")}
          />

          <FlowMessage flow={flow}></FlowMessage>

          <Box space="medium">
            <FormField>
                <InputField
                id="email"
                required
                autoFocus
                aria-required
                type="email"
                autoComplete="username"
                label={t<string>("common.email")}
                check={touchedFields?.identifier && !errors?.identifier}
                aria-invalid={!!errors?.identifier}
                exclaim={!!errors?.identifier}
                {...form.register("identifier", {
                  required: t("common.invalidEmail").toString(),
                  validate: {
                    matchPattern: (v) =>
                      isValidEmail(v) || t("common.invalidEmail").toString(),
                  },
                })}
              />
              {errors?.identifier && (
                <Error id="email">{t("common.invalidEmail").toString()}</Error>
              )}
            </FormField>

            <Button loading={inProgress}>{t("common.continue")}</Button>
          </Box>
        </Box>
      </form>

      <Separator>{t("login.default.separator")}</Separator>

      <Box>
        <Box space="small" className="social-login__wrapper">
          <Button
            type="button"
            variant="light"
            onClick={() => onSocialLogin("apple")}
            loading={selectedSocialProvider == "apple"}
            startIcon={<Icon icon="apple" />}>
            {t("common.loginWithApple")}
          </Button>
          <Button
            type="button"
            variant="light"
            onClick={() => onSocialLogin("facebook")}
            loading={selectedSocialProvider == "facebook"}
            startIcon={<Icon icon="facebook" />}>
            {t("common.loginWithFacebook")}
          </Button>
          <Button
            type="button"
            variant="light"
            onClick={() => onSocialLogin("google")}
            loading={selectedSocialProvider == "google" }
            startIcon={<Icon icon="google" />}>
            {t("common.loginWithGoogle")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export { InputEmail };
